import { FC, forwardRef } from 'react';
import styled from 'styled-components';

import { colors, Body2, fontWeights } from 'ui/theme';
import { TopTooltip } from 'ui/tooltip';

import { SupportedMediums } from 'modules/frequencyManagement/lib/types';
import { lowerCaseMessageMedium } from 'modules/frequencyManagement/lib/utils/mediumDomainUtils';
import { MessageNamesList } from './MessageNamesList';
import { CustomTooltipContent } from './CustomTooltipContent';

const Body2BoldText = styled(Body2)`
  color: ${colors.purple90};
  font-weight: ${fontWeights.semiBold};
  margin: 0;

  &[data-external='true'] {
    color: ${colors.textPrimary};
    font-weight: ${fontWeights.medium};
  }
`;

const ColorControlTooltip = styled.div`
   &[data-external='true'] {
    span {
      color: ${colors.textPrimary};
    }
  }
`;

const ColoredTopTooltip = styled(TopTooltip)`
  color: ${colors.purple90};
`;

export const formatMessageTypeOccurrenceText = (
  occurrenceCount: number,
  zeroCountText: string,
  singularCountText: string,
  pluralCountText: string,
) => {
  if (occurrenceCount === 0) {
    return zeroCountText;
  }

  return occurrenceCount !== 1 ? pluralCountText : singularCountText;
};

export const formatFollowingMediumText = (
  messageMedium: SupportedMediums,
  messageTypeCount: number,
) => {
  const s = messageTypeCount === 1 ? 's' : '';
  return `follow${s} the ${lowerCaseMessageMedium(messageMedium)} frequency rule`;
};

export interface MessageTypeConfig {
  followMediumRuleTextFormatter?: (messageMedium: SupportedMediums, count: number) => string;
  leadingTextFormatter?: (
    count: number,
    zeroCountText: string,
    singularCountText: string,
    pluralCountText: string,
  ) => string;
  ownRulesTextFormatter?: (
    count: number,
    zeroCountText: string,
    singularCountText: string,
    pluralCountText: string,
  ) => string;
}

export interface MessageTypesBreakdownProps extends MessageTypeConfig {
  asExternalContent?: boolean;
  isForOnType: boolean;
  messageMedium: SupportedMediums;
  names: string[];
}
export const MessageTypesBreakdown: FC<MessageTypesBreakdownProps> = ({
  asExternalContent = false,
  followMediumRuleTextFormatter = formatFollowingMediumText,
  isForOnType,
  leadingTextFormatter = formatMessageTypeOccurrenceText,
  messageMedium,
  names,
  ownRulesTextFormatter = formatMessageTypeOccurrenceText,
}) => {
  const count = names.length;
  const messageTypeLeadingCountText = leadingTextFormatter(
    count,
    'No message types',
    `${count} message type`,
    `${count} message types`,
  );
  const messageTypeRuleText = isForOnType
    ? ownRulesTextFormatter(
        count,
        'have their own rules',
        'has its own rule',
        'have their own rules',
      )
    : followMediumRuleTextFormatter(messageMedium, count);
  return (
    <>
      <ColorControlTooltip data-external={asExternalContent}>
        <ColoredTopTooltip
          data-external={asExternalContent}
          ContentComponent={forwardRef(
            (
              // Note: Radix-ui raising warning about inability to pass ref without using forwardRef
              props,

              ref,
            ) => <CustomTooltipContent {...props} names={names} />,
          )}
          tooltipContent={<MessageNamesList names={names} />}
          isEnabled={count !== 0}
          isUnderlined
        >
          <Body2BoldText data-external={asExternalContent}>
            {messageTypeLeadingCountText}
          </Body2BoldText>
        </ColoredTopTooltip>
      </ColorControlTooltip>
      <Body2BoldText data-external={asExternalContent}>{messageTypeRuleText}.</Body2BoldText>
    </>
  );
};
